import { type OperationVariables } from '@apollo/react-common';
import { type MutationOptions } from 'apollo-client';
import { type GraphQLTaggedNode } from 'react-relay';
import { type FetchResult } from 'apollo-link';

import { commitMutation } from './commitMutation.ts';
import { toDocumentNode } from './toDocumentNode.ts';

export type MutateInteropSupportedResult<TData> = Pick<FetchResult<TData>, 'data' | 'errors'>;

// Subset of fields from Apollo's MutationOptions that are supported by mutateInterop
export type MutateInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	MutationOptions<TData, TVariables>,
	| 'variables'
	| 'errorPolicy'
	| 'optimisticResponse'
	| 'update'
	| 'refetchQueries'
	| 'awaitRefetchQueries'
> & {
	mutation: GraphQLTaggedNode;
};

export function mutateInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: MutateInteropSupportedArgs<TData, TVariables>,
): Promise<MutateInteropSupportedResult<TData>> {
	return process.env.BUILD_VARIANT === 'relay'
		? mutateInteropRelay<TData, TVariables>(params)
		: mutateInteropApollo<TData, TVariables>(params);
}

const UNSUPPORTED_RELAY_OPTIONS = [
	'optimisticResponse',
	'update',
	'refetchQueries',
	'awaitRefetchQueries',
];

function mutateInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: MutateInteropSupportedArgs<TData, TVariables>,
): Promise<MutateInteropSupportedResult<TData>> {
	// Check for unsupported options
	const unsupportedOptions = Object.keys(params).filter((key) =>
		UNSUPPORTED_RELAY_OPTIONS.includes(key),
	);
	if (unsupportedOptions.length > 0) {
		throw new Error(`[Relay migration] ${unsupportedOptions} are not supported yet`);
	}

	return commitMutation(window.GLOBAL_RELAY_ENV!, {
		mutation: params.mutation,
		variables: params.variables ?? ({} as TVariables),
	})
		.then((result) => {
			return { data: result as TData };
		})
		.catch((error) => {
			if (params.errorPolicy === 'ignore' || params.errorPolicy === 'all') {
				return { data: null as TData, errors: error.graphQLErrors || [error] };
			}
			throw error;
		});
}

function mutateInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>({
	mutation,
	...rest
}: MutateInteropSupportedArgs<TData, TVariables>): Promise<MutateInteropSupportedResult<TData>> {
	return window['GLOBAL_APOLLO_CLIENT']!.mutate({
		...rest,
		mutation: toDocumentNode(mutation),
	});
}
